import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetByIdLink } from "../../../../app/hooks/useGetByIdLink";
import { useGetChartLink } from "../../../../app/hooks/useGetChartLink";
import { useTransactions } from "../../../../app/hooks/useTransactions";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { selfHireService } from "@/app/services/selfHireService";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTable } from "@/app/hooks/useTable";
import { Table } from "@/app/entities/Table";
import { useLeadMutations } from "@/app/hooks/useLeadMutations";

const updateLinkSchema = z.object({
  label: z.string().min(1, "Nome é obrigatório"),
});

type UpdateLinkFormData = z.infer<typeof updateLinkSchema>;

const useLinkDetailsController = () => {
  const { id: linkId } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { updateLeadStatus } = useLeadMutations();

  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const openLeadDetails = (leadId: string) => {
    setSelectedLeadId(leadId);
    setIsLeadModalOpen(true);
  };

  const closeLeadDetails = () => {
    setIsLeadModalOpen(false);
    setSelectedLeadId(null);
  };

  const [selectedTab, setSelectedTab] = useState<
    "proposals" | "leads" | "settings"
  >("proposals");

  const [selectedTables, setSelectedTables] = useState<string[]>([]);

  const form = useForm<UpdateLinkFormData>({
    resolver: zodResolver(updateLinkSchema),
    mode: "onChange",
    defaultValues: {
      label: "",
    },
  });

  const { tables, isLoadingTables } = useTable();

  function handleOptions(items: Table[]) {
    const byType = items.reduce<Record<string, Table[]>>((acc, item) => {
      acc[item.type || ""] = [...(acc[item.type || ""] || []), item];
      return acc;
    }, {});

    return Object.entries(byType).map(([type, group]) => {
      // a comissão do CLT é por faixa de valor, então não faz sentido exibir a porcentagem
      const isCLT = type.toUpperCase() === "CLT";

      if (group.length === 1) {
        return {
          value: group[0]._id,
          label: group[0].name,
          commission: isCLT ? undefined : group[0].percents,
        };
      }

      return {
        value: type,
        label: type,
        subOptions: group.map((item) => ({
          value: item._id,
          label: item.name,
          commission: isCLT ? undefined : item.percents,
        })),
      };
    });
  }

  const tableOptions = useMemo(() => {
    return tables?.length ? handleOptions(tables) : [];
  }, [tables]);

  const { data: linkData, isLoading: isLoadingLink } = useGetByIdLink({
    linkId: linkId || "",
    enabled: !!linkId,
  });

  const linkTables = useMemo(() => {
    if (!tables?.length || !linkData?.tables?.length) return [];

    return tables
      .filter((table) => linkData.tables.includes(table._id))
      .map((table) => ({
        id: table._id,
        name: table.name,
        commission:
          table.type?.toUpperCase() === "CLT" ? undefined : table.percents,
      }));
  }, [tables, linkData?.tables]);

  useEffect(() => {
    if (linkData) {
      form.reset({
        label: linkData.label || linkData.name || "",
      });
    }
  }, [linkData, form]);

  useEffect(() => {
    if (!linkData?.tables) return;

    setSelectedTables((prev) => {
      const same =
        prev.length === linkData.tables.length &&
        prev.every((v) => linkData.tables.includes(v));

      return same ? prev : linkData.tables;
    });
  }, [linkData?.tables]);

  const { data: chartData, isLoading: isLoadingChart } = useGetChartLink({
    linkId: linkId || "",
    enabled: !!linkId,
  });

  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    setParams,
  } = useTransactions({ interval: false });

  useEffect(() => {
    if (linkId) {
      setParams({
        linkId,
      });
    }
  }, [linkId, setParams]);

  const statistics = transactionsData?.statitics;
  const contracts = statistics?.total || 0;
  const totalValue = statistics?.total_value || 0;

  const simulations = linkData?.simulations_count || 0;

  const chartEntries = chartData?.data || [];
  const totalAccesses = chartData?.total || 0;

  const allTransactions = transactionsData?.transactions || [];

  const getPageIndex = () => {
    if (!transactionsData) return 0;
    return (
      Math.floor(
        transactionsData.pagination.skip / transactionsData.pagination.limit,
      ) + 1
    );
  };

  const getPageCount = () => {
    if (!transactionsData) return 0;
    return Math.ceil(
      transactionsData.pagination.total / transactionsData.pagination.limit,
    );
  };

  const handleNextPage = () => {
    if (!transactionsData) return;
    const newPage =
      transactionsData.pagination.skip / transactionsData.pagination.limit + 1;
    if (getPageCount() === getPageIndex()) return;
    const skipValue = newPage * transactionsData.pagination.limit;
    setParams({ skip: skipValue });
  };

  const handlePreviousPage = () => {
    if (!transactionsData) return;
    const newPage =
      transactionsData.pagination.skip / transactionsData.pagination.limit - 1;
    if (newPage < 0) return;
    const skipValue = newPage * transactionsData.pagination.limit;
    setParams({ skip: skipValue });
  };

  const updateLinkMutation = useMutation({
    mutationFn: ({
      id,
      label,
      tables,
    }: {
      id: string;
      label?: string;
      tables?: string[];
    }) => selfHireService.updateLink(id, { label, tables }),

    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["linkbyId", linkId],
      });

      queryClient.invalidateQueries({
        queryKey: ["AllUserLinks"],
      });

      toast({
        variant: "default",
        title: "Configurações alteradas com sucesso!",
      });
    },

    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: error?.message || "Erro ao alterar configurações",
      });
    },
  });

  const handleUpdateLink = form.handleSubmit(async (data) => {
    await updateLinkMutation.mutateAsync({
      id: linkId || "",
      label: data.label.trim(),
      tables: selectedTables,
    });
  });

  const openWhatsApp = ({ _id: id, phone }: { _id: string; phone: string }) => {
    const cleanedPhone = `55${phone.replace(/\D/g, "").slice(-11)}`;
    const text = "Olá, gostaria de simular uma nova antecipação do FGTS?";
    const encodedText = encodeURIComponent(text);
    const url = `https://wa.me/${cleanedPhone}?text=${encodedText}`;

    handleLeadStatus(id);
    window.open(url, "_blank");
  };

  const handleLeadStatus = async (id: string) => {
    try {
      await updateLeadStatus({ id });

      await queryClient.invalidateQueries({
        queryKey: ["linkbyId", linkId],
      });

      toast({
        variant: "default",
        title: "Lead marcado como contactado!",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar lead",
      });
    }
  };

  return {
    linkData,
    simulations,
    chartData,
    chartEntries,
    totalAccesses,
    transactionsData,
    allTransactions,
    contracts,
    totalValue,
    handleNextPage,
    handlePreviousPage,
    getPageCount,
    getPageIndex,
    isLoadingLink,
    isLoadingChart,
    isLoadingTransactions,
    isLoading: isLoadingLink || isLoadingChart || isLoadingTransactions,
    selectedTab,
    setSelectedTab,
    selectedTables,
    setSelectedTables,
    updateLinkMutation,
    form,
    handleUpdateLink,
    tableOptions,
    isLoadingTables,
    openWhatsApp,
    openLeadDetails,
    closeLeadDetails,
    selectedLeadId,
    isLeadModalOpen,
    linkTables,
  };
};

export default useLinkDetailsController;
